import { useEffect, useMemo, useRef, useState } from "react";
import {
  FiMic,
  FiMicOff,
  FiVideo,
  FiVideoOff,
  FiMonitor,
  FiSend,
  FiUsers,
  FiUserPlus,
  FiCheck,
  FiX,
  FiPaperclip,
  FiDownload,
  FiTrash2,
  FiLoader,
} from "react-icons/fi";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  VideoTrack,
  useLocalParticipant,
  useRoomContext,
  useTracks,
} from "@livekit/components-react";
import { ConnectionState, RoomEvent, Track } from "livekit-client";
import { useTranslation } from "../context/TranslationContext";
import { useSocket } from "../context/SocketContext";
import {
  requestLiveStreamStageJoin,
  decideLiveStreamStageJoin,
  listLiveStreamFiles,
  uploadLiveStreamFile,
  downloadLiveStreamFile,
  deleteLiveStreamFile,
} from "../api/mintSpaceLiveStream";

const formatBytes = (n) => {
  const bytes = Number(n) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function pickParticipantVideoTrack(tracks, identity) {
  const forP = tracks.filter((t) => String(t.participant?.identity) === String(identity));
  const screen = forP.find(
    (t) => t.source === Track.Source.ScreenShare && t.publication?.track
  );
  const camera = forP.find(
    (t) => t.source === Track.Source.Camera && (t.publication?.track || t.participant?.isLocal)
  );
  return screen || camera || forP.find((t) => t.source === Track.Source.Camera);
}

function hasVideoTrack(trackRef) {
  return !!trackRef?.publication?.track;
}

/** Broadcast (host only) or meeting grid when stage speakers are present. */
function LiveStreamVideoLayout({ hostUserId, stagePublisherIds, isDark }) {
  const { t } = useTranslation();
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  const meetingMode = (stagePublisherIds || []).length > 0;
  const mutedText = "text-label-secondary";

  const orderedIdentities = useMemo(() => {
    if (!meetingMode) return [String(hostUserId)];
    const guests = (stagePublisherIds || [])
      .map(String)
      .filter((id) => id && id !== String(hostUserId));
    return [String(hostUserId), ...guests];
  }, [meetingMode, hostUserId, stagePublisherIds]);

  const gridClass =
    orderedIdentities.length <= 1
      ? "grid-cols-1"
      : orderedIdentities.length === 2
        ? "grid-cols-1 sm:grid-cols-2"
        : orderedIdentities.length <= 4
          ? "grid-cols-2"
          : "grid-cols-2 lg:grid-cols-3";

  if (!meetingMode) {
    const main = pickParticipantVideoTrack(tracks, hostUserId);
    if (!hasVideoTrack(main)) {
      return (
        <div
          className={`flex h-full min-h-[200px] items-center justify-center text-subheadline ${mutedText}`}
        >
          {t("liveStream.waitingHostVideo") || "Waiting for host video…"}
        </div>
      );
    }
    return (
      <VideoTrack
        trackRef={main}
        className="h-full w-full object-contain bg-black"
      />
    );
  }

  return (
    <div className={`grid h-full min-h-[200px] auto-rows-fr gap-1.5 p-1.5 ${gridClass}`}>
      {orderedIdentities.map((identity) => {
        const trackRef = pickParticipantVideoTrack(tracks, identity);
        const isHost = identity === String(hostUserId);
        const name =
          trackRef?.participant?.name ||
          trackRef?.participant?.identity ||
          (isHost ? t("liveStream.hostLabel") || "Host" : t("liveStream.speakerLabel") || "Speaker");
        const caption = isHost
          ? `${name} (${t("liveStream.hostLabel") || "Host"})`
          : name;
        return (
          <div
            key={identity}
            className="relative min-h-[140px] overflow-hidden rounded-radius-xs border border-white/10 bg-black"
          >
            {hasVideoTrack(trackRef) ? (
              <VideoTrack trackRef={trackRef} className="h-full w-full object-cover" />
            ) : (
              <div
                className={`flex h-full flex-col items-center justify-center gap-1 px-2 text-center text-footnote ${mutedText}`}
              >
                <FiVideoOff className="h-5 w-5 opacity-60" />
                <span className="truncate">{caption}</span>
              </div>
            )}
            <span className="absolute bottom-0 left-0 right-0 truncate bg-black/65 px-2 py-1 text-caption-1 text-white">
              {caption}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function HostControls({
  isDark,
  onEndLive,
  busy,
  initialCamera = false,
  initialMicrophone = false,
  onDevicesChange,
}) {
  const { t } = useTranslation();
  const { localParticipant } = useLocalParticipant();
  const [camOn, setCamOn] = useState(initialCamera);
  const [micOn, setMicOn] = useState(initialMicrophone);
  const [screenOn, setScreenOn] = useState(false);

  useEffect(() => {
    setCamOn(initialCamera);
    setMicOn(initialMicrophone);
  }, [initialCamera, initialMicrophone]);

  const btn = isDark
    ? "bg-white/10 text-slate-100 hover:bg-white/15"
    : "bg-slate-100 text-slate-800 hover:bg-slate-200";

  const toggleCam = async () => {
    const next = !camOn;
    try {
      await localParticipant.setCameraEnabled(next);
      setCamOn(next);
      onDevicesChange?.({ camera: next, microphone: micOn });
    } catch {
      /* permission or device error */
    }
  };

  const toggleMic = async () => {
    const next = !micOn;
    try {
      await localParticipant.setMicrophoneEnabled(next);
      setMicOn(next);
      onDevicesChange?.({ camera: camOn, microphone: next });
    } catch {
      /* permission or device error */
    }
  };

  const toggleScreen = async () => {
    if (screenOn) {
      await localParticipant.setScreenShareEnabled(false);
      setScreenOn(false);
      return;
    }
    await localParticipant.setScreenShareEnabled(true);
    setScreenOn(true);
  };

  return (
    <div
      className={`flex shrink-0 flex-wrap items-center justify-between gap-2 border-t px-3 py-2 ${
        "border-separator-default bg-bg-inset"
      }`}
    >
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={toggleCam} className={`rounded-radius-xs p-2 ${btn}`}>
          {camOn ? <FiVideo className="h-4 w-4" /> : <FiVideoOff className="h-4 w-4" />}
        </button>
        <button type="button" onClick={toggleMic} className={`rounded-radius-xs p-2 ${btn}`}>
          {micOn ? <FiMic className="h-4 w-4" /> : <FiMicOff className="h-4 w-4" />}
        </button>
        <button type="button" onClick={toggleScreen} className={`rounded-radius-xs p-2 ${btn}`}>
          <FiMonitor className="h-4 w-4" />
        </button>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={onEndLive}
        className="rounded-radius-xs bg-status-danger px-3 py-1.5 text-footnote-semibold text-white hover:opacity-90 disabled:opacity-50"
      >
        {t("liveStream.endLive") || "End live"}
      </button>
    </div>
  );
}

/** Apply device choices once per LiveKit connect (toggles use setCameraEnabled directly). */
function InitialLiveKitDevices({ sessionKey, camera, microphone, enabled }) {
  const room = useRoomContext();
  const appliedForKey = useRef(null);

  useEffect(() => {
    appliedForKey.current = null;
  }, [sessionKey]);

  useEffect(() => {
    if (!enabled || !room) return;

    const applyDevices = async () => {
      if (appliedForKey.current === sessionKey) return;
      try {
        await room.localParticipant.setCameraEnabled(!!camera);
        await room.localParticipant.setMicrophoneEnabled(!!microphone);
        appliedForKey.current = sessionKey;
      } catch {
        appliedForKey.current = null;
      }
    };

    if (room.state === ConnectionState.Connected) {
      void applyDevices();
    }

    const onReady = () => void applyDevices();
    room.on(RoomEvent.Connected, onReady);
    room.on(RoomEvent.Reconnected, onReady);
    return () => {
      room.off(RoomEvent.Connected, onReady);
      room.off(RoomEvent.Reconnected, onReady);
    };
  }, [enabled, sessionKey, camera, microphone, room]);

  return null;
}

function LocalSelfPreview() {
  const tracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: false }], {
    onlySubscribed: false,
  });
  const local = tracks.find((t) => t.participant?.isLocal);
  if (!local) return null;
  return (
    <div className="pointer-events-none absolute bottom-14 right-3 z-10 w-28 overflow-hidden rounded-radius-xs border border-white/20 shadow-lg">
      <VideoTrack trackRef={local} className="aspect-video w-full bg-black object-cover" />
    </div>
  );
}

function StageControls({
  isDark,
  initialCamera = false,
  initialMicrophone = false,
  onDevicesChange,
  meetingMode = false,
}) {
  const { localParticipant } = useLocalParticipant();
  const [camOn, setCamOn] = useState(initialCamera);
  const [micOn, setMicOn] = useState(initialMicrophone);

  useEffect(() => {
    setCamOn(initialCamera);
    setMicOn(initialMicrophone);
  }, [initialCamera, initialMicrophone]);

  const btn = isDark
    ? "bg-white/10 text-slate-100 hover:bg-white/15"
    : "bg-slate-100 text-slate-800 hover:bg-slate-200";

  const toggleCam = async () => {
    const next = !camOn;
    try {
      await localParticipant.setCameraEnabled(next);
      setCamOn(next);
      onDevicesChange?.({ camera: next, microphone: micOn });
    } catch {
      /* permission or device error */
    }
  };

  const toggleMic = async () => {
    const next = !micOn;
    try {
      await localParticipant.setMicrophoneEnabled(next);
      setMicOn(next);
      onDevicesChange?.({ camera: camOn, microphone: next });
    } catch {
      /* permission or device error */
    }
  };

  return (
    <>
      {camOn && !meetingMode ? <LocalSelfPreview /> : null}
      <div
        className={`flex shrink-0 items-center gap-2 border-t px-3 py-2 ${
          "border-separator-default bg-bg-inset"
        }`}
      >
        <button type="button" onClick={toggleCam} className={`rounded-radius-xs p-2 ${btn}`}>
          {camOn ? <FiVideo className="h-4 w-4" /> : <FiVideoOff className="h-4 w-4" />}
        </button>
        <button type="button" onClick={toggleMic} className={`rounded-radius-xs p-2 ${btn}`}>
          {micOn ? <FiMic className="h-4 w-4" /> : <FiMicOff className="h-4 w-4" />}
        </button>
      </div>
    </>
  );
}

function LiveStreamSidePanel({
  isDark,
  mintSpaceId,
  instanceId,
  userId,
  displayName,
  isHost,
  joinRequests,
  onJoinRequestsChange,
  stageRequestStatus,
  setStageRequestStatus,
  canPublishStage,
  setCanPublishStage,
  viewerNames,
  viewerCount,
  setToast,
  onOpenStageDevicePrompt,
  onSessionUpdate,
  role = "viewer",
  stagePublisherIds = [],
}) {
  const { t } = useTranslation();
  const { socket } = useSocket();
  const [chatInput, setChatInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const mutedText = "text-label-secondary";
  const baseText = "text-label-primary";
  const panelBg = "border-separator-default bg-bg-raised";
  const inputClass = "border-separator-default bg-bg-raised text-label-primary placeholder-label-tertiary";

  useEffect(() => {
    if (!socket || !mintSpaceId || !instanceId) return undefined;

    socket.emit("livestream_join", {
      mintSpaceId,
      instanceId,
      displayName,
    });

    const onChat = (msg) => {
      setMessages((prev) => [...prev.slice(-199), msg]);
    };
    const onPresence = ({ viewers, count }) => {
      if (viewers) {
        // parent could use this — handled via props from parent state
      }
      void count;
    };
    const onJoinRequest = ({ session }) => {
      onJoinRequestsChange?.(session?.joinRequests ?? []);
      onSessionUpdate?.(session);
    };
    const onDecision = ({ userId: uid, accept, session }) => {
      onJoinRequestsChange?.(session?.joinRequests ?? []);
      onSessionUpdate?.(session);
      const isMe = uid == null || String(uid) === String(userId);
      if (!isMe) return;
      setStageRequestStatus(accept ? "accepted" : "declined");
      if (accept) {
        setCanPublishStage(true);
        onOpenStageDevicePrompt?.();
      } else {
        setToast?.(t("liveStream.stageDenied") || "Your join request was declined");
      }
    };

    const onFiles = ({ files: next }) => {
      if (Array.isArray(next)) setFiles(next);
    };

    socket.on("livestream_chat", onChat);
    socket.on("livestream_presence", onPresence);
    socket.on("livestream_join_request", onJoinRequest);
    socket.on("livestream_stage_decision", onDecision);
    socket.on("livestream_files", onFiles);

    return () => {
      socket.emit("livestream_leave", { mintSpaceId, instanceId });
      socket.off("livestream_chat", onChat);
      socket.off("livestream_presence", onPresence);
      socket.off("livestream_join_request", onJoinRequest);
      socket.off("livestream_stage_decision", onDecision);
      socket.off("livestream_files", onFiles);
    };
  }, [
    socket,
    mintSpaceId,
    instanceId,
    displayName,
    userId,
    onJoinRequestsChange,
    setStageRequestStatus,
    setCanPublishStage,
    onOpenStageDevicePrompt,
    onSessionUpdate,
    setToast,
    t,
  ]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!mintSpaceId || !instanceId) return;
      try {
        setFilesLoading(true);
        const res = await listLiveStreamFiles(mintSpaceId, instanceId);
        if (!cancelled && res.success) setFiles(res.data.files || []);
      } catch {
        if (!cancelled) setFiles([]);
      } finally {
        if (!cancelled) setFilesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mintSpaceId, instanceId]);

  const sendChat = (e) => {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text || !socket) return;
    socket.emit("livestream_chat", { mintSpaceId, instanceId, text });
    setChatInput("");
  };

  const requestStage = async () => {
    try {
      const res = await requestLiveStreamStageJoin(mintSpaceId, instanceId);
      if (res.success) {
        if (res.data.status === "accepted") {
          setStageRequestStatus("accepted");
          setCanPublishStage(true);
          onSessionUpdate?.(res.data.session);
          onOpenStageDevicePrompt?.();
          setToast?.(
            t("liveStream.devicePromptStageOpen") ||
              "Choose your camera and microphone, then continue."
          );
        } else {
          setStageRequestStatus("pending");
        }
      }
    } catch {
      /* toast handled by caller if needed */
    }
  };

  const handleUploadFile = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked || !mintSpaceId || !instanceId) return;
    setUploadingFile(true);
    try {
      const res = await uploadLiveStreamFile(mintSpaceId, instanceId, picked);
      if (res.success) {
        setToast?.(t("liveStream.fileUploaded") || "File uploaded");
        const list = await listLiveStreamFiles(mintSpaceId, instanceId);
        if (list.success) setFiles(list.data.files || []);
      }
    } catch (err) {
      setToast?.(
        err.response?.data?.message ||
          err.message ||
          t("liveStream.fileUploadFailed") ||
          "Upload failed"
      );
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      const blob = await downloadLiveStreamFile(mintSpaceId, file._id, instanceId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name || "download";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setToast?.(t("liveStream.fileDownloadFailed") || "Download failed");
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await deleteLiveStreamFile(mintSpaceId, fileId, instanceId);
      setFiles((prev) => prev.filter((f) => f._id !== fileId));
      setToast?.(t("liveStream.fileRemoved") || "File removed");
    } catch {
      setToast?.(t("liveStream.fileRemoveFailed") || "Could not remove file");
    }
  };

  const decide = async (targetUserId, accept) => {
    if (!targetUserId) return;
    try {
      const res = await decideLiveStreamStageJoin(mintSpaceId, instanceId, {
        userId: String(targetUserId),
        accept: !!accept,
      });
      if (res.success) {
        onJoinRequestsChange?.(res.data?.session?.joinRequests ?? []);
        onSessionUpdate?.(res.data?.session);
        setToast?.(
          accept
            ? t("liveStream.joinAccepted") || "Join request accepted"
            : t("liveStream.joinDeclined") || "Join request declined"
        );
      }
    } catch (err) {
      setToast?.(
        err.response?.data?.message ||
          err.message ||
          t("liveStream.joinDecideFailed") ||
          "Could not update join request"
      );
    }
  };

  return (
    <aside
      className={`flex w-full min-h-0 flex-col border-l sm:w-72 lg:w-80 ${panelBg}`}
    >
      <div className={`border-b px-3 py-2.5 border-separator-default`}>
        <div className={`flex items-center gap-2 text-footnote-semibold ${baseText}`}>
          <FiUsers className="h-3.5 w-3.5 text-accent-text" />
          {t("liveStream.watching") || "Watching"}
          <span className={`ml-auto rounded-full bg-accent-tinted px-2 py-0.5 text-accent-text`}>
            {viewerCount}
          </span>
        </div>
        <ul className={`mt-2 max-h-24 space-y-1 overflow-y-auto text-caption-1 ${mutedText}`}>
          {viewerNames.map((v) => (
            <li key={v.userId} className="truncate">
              {v.name}
            </li>
          ))}
          {viewerNames.length === 0 && (
            <li>{t("liveStream.noViewersYet") || "No viewers yet"}</li>
          )}
        </ul>
      </div>

      {isHost && joinRequests?.length > 0 && (
        <div className={`border-b px-3 py-2 border-separator-default`}>
          <p className={`mb-2 text-footnote-semibold ${baseText}`}>
            {t("liveStream.joinRequests") || "Join requests"}
          </p>
          <ul className="space-y-2">
            {joinRequests.map((r) => (
              <li
                key={r.user?._id}
                className={`flex items-center gap-2 rounded-radius-xs border px-2 py-1.5 text-footnote ${
                  "border-separator-default"
                }`}
              >
                <span className={`min-w-0 flex-1 truncate ${baseText}`}>
                  {r.user?.displayName || r.user?.username || "Member"}
                </span>
                <button
                  type="button"
                  aria-label="Accept"
                  onClick={() => decide(r.user._id, true)}
                  className="rounded p-1 text-status-success hover:bg-emerald-500/10"
                >
                  <FiCheck className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  aria-label="Decline"
                  onClick={() => decide(r.user._id, false)}
                  className="rounded p-1 text-status-danger hover:bg-status-danger-bg"
                >
                  <FiX className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isHost &&
        userId &&
        (stagePublisherIds || []).some((id) => String(id) === String(userId)) &&
        role !== "stage" && (
          <div className={`border-b px-3 py-2 border-separator-default`}>
            <button
              type="button"
              onClick={() => onOpenStageDevicePrompt?.()}
              className="flex w-full items-center justify-center gap-2 rounded-radius-sm bg-accent-default px-3 py-2.5 text-footnote-semibold text-accent-on hover:brightness-105"
            >
              <FiVideo className="h-3.5 w-3.5" />
              {t("liveStream.finishSpeakerSetup") || "Set up camera & microphone"}
            </button>
          </div>
        )}

      {!isHost && !canPublishStage && role !== "stage" && (
        <div className={`border-b px-3 py-2 border-separator-default`}>
          <button
            type="button"
            disabled={stageRequestStatus === "pending"}
            onClick={requestStage}
            className={`flex w-full items-center justify-center gap-2 rounded-radius-sm border px-3 py-2 text-footnote-semibold transition disabled:opacity-50 ${
              isDark
                ? "border-white/15 text-slate-200 hover:bg-white/5"
                : "border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            <FiUserPlus className="h-3.5 w-3.5" />
            {stageRequestStatus === "pending"
              ? t("liveStream.requestPending") || "Request sent…"
              : stageRequestStatus === "declined"
                ? t("liveStream.requestDeclined") || "Request declined"
                : t("liveStream.requestToJoin") || "Request to join"}
          </button>
        </div>
      )}

      <div
        className={`border-b px-3 py-2 border-separator-default`}
      >
        <div className="mb-2 flex items-center gap-2">
          <FiPaperclip className="h-3.5 w-3.5 text-accent-text" />
          <p className={`text-footnote-semibold ${baseText}`}>
            {t("liveStream.sharedFiles") || "Shared files"}
          </p>
          {isHost && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleUploadFile}
              />
              <button
                type="button"
                disabled={uploadingFile}
                onClick={() => fileInputRef.current?.click()}
                className={`ml-auto rounded-radius-xs px-2 py-1 text-caption-1-semibold ${
                  isDark
                    ? "bg-white/10 text-slate-200 hover:bg-white/15"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                } disabled:opacity-50`}
              >
                {uploadingFile ? (
                  <FiLoader className="h-3 w-3 animate-spin" />
                ) : (
                  t("liveStream.uploadFile") || "Upload"
                )}
              </button>
            </>
          )}
        </div>
        {filesLoading ? (
          <p className={`text-caption-1 ${mutedText}`}>{t("common.loading") || "Loading…"}</p>
        ) : files.length === 0 ? (
          <p className={`text-caption-1 ${mutedText}`}>
            {t("liveStream.noFilesYet") || "No files shared yet"}
          </p>
        ) : (
          <ul className="max-h-28 space-y-1.5 overflow-y-auto">
            {files.map((f) => (
              <li
                key={f._id}
                className={`flex items-center gap-1.5 rounded-radius-xs border px-2 py-1.5 text-caption-1 ${
                  "border-separator-default"
                }`}
              >
                <span className={`min-w-0 flex-1 truncate ${baseText}`} title={f.name}>
                  {f.name}
                </span>
                <span className={mutedText}>{formatBytes(f.size)}</span>
                <button
                  type="button"
                  aria-label="Download"
                  onClick={() => handleDownload(f)}
                  className="rounded p-0.5 text-accent-text hover:bg-accent-tinted"
                >
                  <FiDownload className="h-3.5 w-3.5" />
                </button>
                {isHost && (
                  <button
                    type="button"
                    aria-label="Remove"
                    onClick={() => handleDeleteFile(f._id)}
                    className="rounded p-0.5 text-status-danger hover:bg-status-danger-bg"
                  >
                    <FiTrash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <p className={`px-3 py-2 text-footnote-semibold ${baseText}`}>
          {t("liveStream.chat") || "Chat"}
        </p>
        <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-2">
          {messages.map((m) => (
            <div key={m.id} className="mb-2 text-footnote">
              <span className="font-semibold text-accent-text">
                {String(m.userId) === String(userId)
                  ? t("liveStream.you") || "You"
                  : m.name || t("liveStream.viewer") || "Viewer"}
              </span>
              <p className={`${baseText} break-words`}>{m.text}</p>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        <form
          onSubmit={sendChat}
          className={`flex gap-2 border-t p-2 border-separator-default`}
        >
          <input
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            maxLength={500}
            placeholder={t("liveStream.chatPlaceholder") || "Say something…"}
            className={`min-w-0 flex-1 rounded-radius-xs border px-2 py-1.5 text-footnote outline-none ${inputClass}`}
          />
          <button
            type="submit"
            className="rounded-radius-xs bg-accent-default px-2.5 py-1.5 text-accent-on hover:brightness-105"
          >
            <FiSend className="h-3.5 w-3.5" />
          </button>
        </form>
      </div>
    </aside>
  );
}

function BroadcastRoomInner({
  isDark,
  role,
  hostUserId,
  userId,
  mintSpaceId,
  instanceId,
  displayName,
  joinRequests,
  onJoinRequestsChange,
  onEndLive,
  busy,
  canPublishInitial,
  setToast,
  onOpenStageDevicePrompt,
  liveKitSessionKey,
  token,
  serverUrl,
  publishCamera,
  publishMicrophone,
  onDisconnected,
  stagePublisherIds = [],
  onSessionUpdate,
}) {
  const isHost = role === "host";
  const isStage = role === "stage";
  const canSpeak = isHost || isStage;
  const meetingMode = stagePublisherIds.length > 0;
  const [liveDevices, setLiveDevices] = useState({
    camera: publishCamera,
    microphone: publishMicrophone,
  });
  const [canPublishStage, setCanPublishStage] = useState(canSpeak);
  const [stageRequestStatus, setStageRequestStatus] = useState(
    isStage ? "accepted" : "none"
  );

  useEffect(() => {
    setLiveDevices({ camera: publishCamera, microphone: publishMicrophone });
  }, [liveKitSessionKey, publishCamera, publishMicrophone]);

  useEffect(() => {
    if (canSpeak) setCanPublishStage(true);
  }, [canSpeak]);

  const handleDevicesChange = (patch) => {
    setLiveDevices((prev) => ({ ...prev, ...patch }));
  };
  const [viewerNames, setViewerNames] = useState([]);
  const [viewerCount, setViewerCount] = useState(0);
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return undefined;
    const onPresence = ({ viewers, count }) => {
      setViewerNames(viewers || []);
      setViewerCount(typeof count === "number" ? count : (viewers || []).length);
    };
    socket.on("livestream_presence", onPresence);
    return () => socket.off("livestream_presence", onPresence);
  }, [socket]);

  const videoStage = (
    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-black">
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1">
          <LiveStreamVideoLayout
            hostUserId={hostUserId}
            stagePublisherIds={stagePublisherIds}
            isDark={isDark}
          />
        </div>
      </div>
      <RoomAudioRenderer />
      {isHost && (
        <HostControls
          isDark={isDark}
          onEndLive={onEndLive}
          busy={busy}
          initialCamera={liveDevices.camera}
          initialMicrophone={liveDevices.microphone}
          onDevicesChange={handleDevicesChange}
        />
      )}
      {!isHost && isStage && (
        <StageControls
          isDark={isDark}
          initialCamera={liveDevices.camera}
          initialMicrophone={liveDevices.microphone}
          onDevicesChange={handleDevicesChange}
          meetingMode={meetingMode}
        />
      )}
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
      <LiveKitRoom
        key={liveKitSessionKey}
        token={token}
        serverUrl={serverUrl}
        connect
        video={false}
        audio={false}
        onDisconnected={onDisconnected}
        className="relative flex min-h-0 min-w-0 flex-1 flex-col"
      >
        <InitialLiveKitDevices
          sessionKey={liveKitSessionKey}
          camera={liveDevices.camera}
          microphone={liveDevices.microphone}
          enabled={canSpeak}
        />
        {videoStage}
      </LiveKitRoom>
      <LiveStreamSidePanel
        isDark={isDark}
        mintSpaceId={mintSpaceId}
        instanceId={instanceId}
        userId={userId}
        displayName={displayName}
        isHost={isHost}
        joinRequests={joinRequests}
        onJoinRequestsChange={onJoinRequestsChange}
        stageRequestStatus={stageRequestStatus}
        setStageRequestStatus={setStageRequestStatus}
        canPublishStage={canPublishStage}
        setCanPublishStage={setCanPublishStage}
        viewerNames={viewerNames}
        viewerCount={viewerCount}
        setToast={setToast}
        onOpenStageDevicePrompt={onOpenStageDevicePrompt}
        onSessionUpdate={onSessionUpdate}
        role={role}
        stagePublisherIds={stagePublisherIds}
      />
    </div>
  );
}

/**
 * Broadcast-style live room: one host video, viewer list, chat, optional stage mic after approval.
 */
export default function LiveStreamBroadcastRoom({
  liveKitSessionKey = 0,
  token,
  serverUrl,
  role,
  hostUserId,
  userId,
  displayName,
  mintSpaceId,
  instanceId,
  isDark,
  joinRequests,
  onJoinRequestsChange,
  onEndLive,
  busy,
  canPublish,
  onDisconnected,
  publishCamera = false,
  publishMicrophone = false,
  onOpenStageDevicePrompt,
  setToast,
  stagePublisherIds,
  onSessionUpdate,
}) {
  return (
    <BroadcastRoomInner
      isDark={isDark}
      role={role}
      hostUserId={hostUserId}
      userId={userId}
      mintSpaceId={mintSpaceId}
      instanceId={instanceId}
      displayName={displayName}
      joinRequests={joinRequests}
      onJoinRequestsChange={onJoinRequestsChange}
      onEndLive={onEndLive}
      busy={busy}
      canPublishInitial={canPublish}
      setToast={setToast}
      onOpenStageDevicePrompt={onOpenStageDevicePrompt}
      liveKitSessionKey={liveKitSessionKey}
      token={token}
      serverUrl={serverUrl}
      publishCamera={publishCamera}
      publishMicrophone={publishMicrophone}
      onDisconnected={onDisconnected}
      stagePublisherIds={stagePublisherIds}
      onSessionUpdate={onSessionUpdate}
    />
  );
}
